// # GLOBAL NETWORK: layout is presentation only; preserve the real referral
// parent and depth supplied by the API, including when opening a smaller branch.
export const NETWORK_ROOT = "__webprofit__";
const time = (value) => new Date(value || 0).getTime() || 0;

export function changedNetworkNodes(previous, next) {
  if (!previous) return [];
  const old = new Map((previous.nodes || []).map((node) => [node.id, node]));
  const previousEvents = new Set((previous.events || []).map((event) => event.id));
  const newEventNodes = (next.events || []).filter((event) => !previousEvents.has(event.id)).flatMap((event) => event.nodeIds || []);
  return [...new Set([...newEventNodes, ...(next.nodes || []).filter((node) => {
    const prior = old.get(node.id);
    return !prior || time(node.lastPersonalAt) > time(prior.lastPersonalAt)
      || time(node.lastBusinessAt) > time(prior.lastBusinessAt)
      || node.personalEvents > prior.personalEvents || node.businessEvents > prior.businessEvents;
  }).map((node) => node.id)])];
}

export function networkPulsePath(ids, nodes) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const result = new Set(ids.length ? [NETWORK_ROOT] : []);
  for (const id of ids) {
    let current = byId.get(id);
    const visited = new Set();
    while (current && !visited.has(current.id)) {
      visited.add(current.id); result.add(current.id); current = byId.get(current.parentId);
    }
  }
  return [...result];
}

export function networkLayout(nodes, focusId = "", { collapsedIds = [] } = {}) {
  const collapsed = new Set(collapsedIds);
  const children = new Map();
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const sorted = [...nodes].sort((a, b) => Number(b.isSeller) - Number(a.isSeller) || a.name.localeCompare(b.name, "es") || a.id.localeCompare(b.id));
  for (const node of sorted) {
    const parent = byId.has(node.parentId) ? node.parentId : NETWORK_ROOT;
    const list = children.get(parent) || []; list.push(node); children.set(parent, list);
  }
  let level = focusId && byId.has(focusId) ? [byId.get(focusId)] : children.get(NETWORK_ROOT) || [];
  const levels = [];
  let shown = 0;
  while (level.length && levels.length < 5 && shown < 88) {
    const visible = level.slice(0, Math.min(8 + levels.length * 6, 88 - shown));
    levels.push(visible); shown += visible.length;
    level = visible.filter((node) => !collapsed.has(node.id)).flatMap((node) => children.get(node.id) || []);
  }
  const width = 1200, rowHeight = 142;
  const placed = [{ id: NETWORK_ROOT, name: "WebProfit", x: width / 2, y: 66, displayLevel: 0, depth: 0 }];
  levels.forEach((row, index) => {
    // Extra rows keep dense levels readable while the envelope widens downward.
    const columns = Math.min(10, 8 + index * 2, row.length);
    const rowWidth = Math.min(1120, Math.max(430 + index * 230, columns * 112));
    const offset = levels.slice(0, index).reduce((sum, items, i) => sum + Math.ceil(items.length / Math.min(10, 8 + i * 2)), 0);
    row.forEach((node, i) => {
      const subrow = Math.floor(i / columns), count = Math.min(columns, row.length - subrow * columns);
      placed.push({ ...node, collapsed: collapsed.has(node.id), x: width / 2 + ((i % columns + .5) / count - .5) * rowWidth,
        y: 66 + (offset + subrow + 1) * rowHeight, displayLevel: index + 1 });
    });
  });
  const placedById = new Map(placed.map((node) => [node.id, node]));
  const edges = placed.slice(1).map((node) => ({ from: placedById.get(node.parentId) || placed[0], to: node,
    referral: placedById.has(node.parentId) && node.parentId !== NETWORK_ROOT }));
  return { nodes: placed, edges, width, height: Math.max(515, ...placed.map((node) => node.y + 86)),
    hidden: nodes.length - shown, focus: byId.get(focusId) || null };
}

// # GLOBAL NETWORK V2: navigation follows original edges even in a collapsed view.
export function networkAncestors(nodes, id) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const path = [], visited = new Set();
  let current = byId.get(id);
  while (current && !visited.has(current.id)) {
    visited.add(current.id); path.unshift(current); current = byId.get(current.parentId);
  }
  return path;
}

export function networkDescendantIds(nodes, id) {
  if (!id) return new Set(nodes.map((node) => node.id));
  const children = new Map();
  for (const node of nodes) {
    if (!children.has(node.parentId)) children.set(node.parentId, []);
    children.get(node.parentId).push(node.id);
  }
  const found = new Set(), pending = [id];
  for (let index = 0; index < pending.length; index += 1) {
    const current = pending[index];
    if (found.has(current)) continue;
    found.add(current); pending.push(...(children.get(current) || []));
  }
  return found;
}

export function networkEventKind(event = {}) {
  if (["warning", "critical"].includes(event.severity)) return { key: "signal", label: "Señal para revisar", color: "#FFB966" };
  if (["user", "auth", "project"].includes(event.category)) return { key: "growth", label: "Personas y proyectos", color: "#F8896B" };
  if (["order", "payment", "erp", "cash", "stock", "appointment"].includes(event.category)) return { key: "operation", label: "Operación del negocio", color: "#78E9BC" };
  if (event.category === "navigation") return { key: "navigation", label: "Navegación", color: "#7ECDF5" };
  return { key: "activity", label: "Actividad de la plataforma", color: "#B9C7EB" };
}

export function nodeState(node, generatedAt) {
  if (node.status === "suspended" || node.sellerStatus === "suspended") return "suspended";
  if (node.hasFriction) return "friction";
  const cutoff = time(generatedAt) - 5 * 60000;
  if (time(node.lastPersonalAt) >= cutoff) return "personal";
  if (time(node.lastBusinessAt) >= cutoff) return "business";
  return "quiet";
}
